import { ChangeEvent, MouseEvent, useRef, useState, useEffect } from 'react';
import styles from './Select.module.css';
import { IconCaretDownFilled } from '@tabler/icons-react';
import { StyleProps } from './types';

type Props = StyleProps & {
	placeholder?: string;
	options: Option[];
	frameless?: boolean;
	value?: string;
	onSelect?: (value: string) => void;
};

const Select = ({ placeholder, options, frameless, value, onSelect, className, style }: Props) =>
{
	const selectedOption = value ? options.find(o => o.value === value) : undefined;
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const selectRef = useRef<HTMLSelectElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() =>
	{
		document.addEventListener('mousedown', handleClickOutside);
		return () =>
		{
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const handleSelect = (event: ChangeEvent<HTMLSelectElement>) =>
	{
		onSelect?.(event.target.value);
	};

	const handleClickSelect = (_event: MouseEvent<HTMLDivElement>) =>
	{
		setIsOpen(!isOpen);
	};

	const handleClickOption = (event: MouseEvent<HTMLDivElement>) =>
	{
		event.stopPropagation();
		const selectedValue = event.currentTarget.getAttribute('data-option-value');
		if (selectedValue)
		{
			onSelect?.(selectedValue);
		}
		setIsOpen(false);
	};

	const handleClickOutside = (event: globalThis.MouseEvent) =>
	{
		if (containerRef.current && !containerRef.current.contains(event.target as Node))
		{
			setIsOpen(false);
		}
	};

	return (
		<div ref={containerRef} className={`${styles.select} ${isOpen ? styles.open : ''} ${frameless ? styles.frameless : ''}${className ? ` ${className}` : ''}`} style={style} onClick={handleClickSelect}>
			<div>{selectedOption?.label || placeholder || 'Select an option'}</div>
			{isOpen && <div className={styles.dropdown}>
				{options.map(option => (
					<div className={styles.option} data-option-value={option.value} key={option.value} onClick={handleClickOption}>{option.label}</div>
				))}
			</div>}
			<IconCaretDownFilled />
			<select ref={selectRef} onChange={handleSelect} defaultValue="placeholder">
				<option value={value} disabled>{selectedOption?.label || placeholder || 'Select an option'}</option>
				{options.map(option => (
					<option key={option.value} value={option.value}>{option.label}</option>
				))}
			</select>
		</div>
	);
};

type Option = {
	value: string;
	label: string;
};

export default Select;